import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowUturnLeftIcon,
  ArrowsRightLeftIcon,
  CheckIcon,
  ExclamationTriangleIcon,
  MagnifyingGlassIcon,
  PencilSquareIcon,
  PlusIcon,
  SparklesIcon,
  TagIcon,
  TrashIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { TAG_DESCRIPTION_MAX, TAG_LABEL_MAX, type TaxonomyTag } from '@xphoto/shared';
import { useI18n } from '../i18n';
import { interpolate } from '../gallery/format';
import { ApiError } from '../gallery/api';
import { recordAction } from '../diagnostics/buffers';
import { Portal } from '../components/Portal';
import { ConfirmModal } from './ConfirmModal';
import {
  createTag,
  deleteTag,
  fetchTaxonomy,
  mergeTags,
  renameTag,
  setTagDescription,
} from './taxonomy-api';

/**
 * Écran « TAXONOMIE » — gestion du vocabulaire de tags (T5 lot B, XPHOTO-26).
 *
 * PAGE (comme l'Outil EXIF / le Dédoublonnage) rendue dans la zone `main` sous l'en-tête
 * persistant (`AppChrome`) : le fil d'Ariane (« Accueil / Taxonomie ») pilote le retour ;
 * la page défile via son propre conteneur `overflow-y-auto`.
 *
 * Taxonomie STRICTEMENT PLATE : pas de catégories, pas de hiérarchie, un seul libellé
 * par tag. Fonctions : lister (avec nombre d'usages), rechercher/filtrer, trier par
 * nom ou par usage, créer, renommer en ligne, supprimer (avertit si utilisé), et
 * FUSIONNER plusieurs tags dans un tag cible conservé.
 */
interface TaxonomyPageProps {
  libraryId: string;
  onClose: () => void;
}

/** Critère de tri de la liste. */
type SortKey = 'name' | 'usage';

/**
 * Un tag « candidat » = proposé par l'IA (≥ 1 proposition en attente) mais JAMAIS validé
 * (aucun usage validé). Ces tags n'existent dans le vocabulaire que parce que le lot C a
 * créé leur ligne `tags` en stockant une proposition — ils ne sont pas encore « adoptés ».
 */
function isCandidate(tag: TaxonomyTag): boolean {
  return tag.usageCount === 0 && tag.proposedCount > 0;
}

const inputClass =
  'h-11 rounded-md border border-border-strong bg-surface px-3 text-sm text-fg placeholder:text-muted focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand';

/** Trie une COPIE des tags selon le critère (nom = insensible casse/accents ; usage desc). */
function sortTags(tags: TaxonomyTag[], key: SortKey): TaxonomyTag[] {
  const copy = [...tags];
  if (key === 'usage') {
    // Plus utilisés d'abord ; départage par nom pour un ordre stable.
    copy.sort(
      (a, b) =>
        b.usageCount - a.usageCount ||
        a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }),
    );
  } else {
    copy.sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
  }
  return copy;
}

export function TaxonomyPage({ libraryId, onClose }: TaxonomyPageProps): React.JSX.Element {
  const { t, locale } = useI18n();

  const [tags, setTags] = useState<TaxonomyTag[] | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [query, setQuery] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('name');
  // Masque les tags « candidats » (proposés par l'IA, jamais validés).
  const [hideCandidates, setHideCandidates] = useState(false);

  // Création.
  const [newLabel, setNewLabel] = useState('');
  const [createError, setCreateError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  // Renommage en ligne : id du tag en cours d'édition + valeur + erreur.
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editLabel, setEditLabel] = useState('');
  const [editError, setEditError] = useState<string | null>(null);
  const [editBusy, setEditBusy] = useState(false);

  // Édition de la DESCRIPTION (définition libre qui guide l'IA de tagging) : brouillon par
  // tag, id en cours d'enregistrement, id en erreur. Optionnelle et discrète : la plupart
  // des tags n'en ont pas.
  const [descDrafts, setDescDrafts] = useState<Record<string, string>>({});
  const [descSavingId, setDescSavingId] = useState<string | null>(null);
  const [descErrorId, setDescErrorId] = useState<string | null>(null);

  /** Valeur affichée dans le champ description d'un tag : brouillon local ou valeur serveur. */
  const descValueOf = (tag: TaxonomyTag): string => descDrafts[tag.id] ?? tag.description ?? '';

  // Miroirs en `ref` des états que les rappels stables doivent LIRE sans en dépendre.
  // Sans eux, `saveDescription` (qui lit `descDrafts`) et `submitEdit` (qui lit
  // `editBusy`) changeraient d'identité à chaque frappe, ce qui casserait la
  // mémoïsation des lignes (cf. `TaxonomyRow`) et re-rendrait toute la liste.
  const descDraftsRef = useRef(descDrafts);
  descDraftsRef.current = descDrafts;
  const editBusyRef = useRef(editBusy);
  editBusyRef.current = editBusy;

  /**
   * Garde de RÉ-ENTRANCE (audit F5) — un `ref`, pas l'état `descSavingId` : elle doit
   * être posée SYNCHRONEMENT, avant tout rendu.
   *
   * `saveDescription` est câblée sur `onSubmit` ET sur `onBlur` du MÊME champ. Valider
   * par Entrée déclenchait la séquence : `onSubmit` → champ FOCALISÉ rendu `disabled`
   * → le navigateur émet `blur` → `onBlur` rappelle `saveDescription`. Les deux gardes
   * de sortie existantes étaient franchies (le brouillon n'est retiré de `descDrafts`
   * qu'APRÈS l'`await`, et `tag.description` porte encore l'ancienne valeur) →
   * DOUBLE PATCH garanti.
   */
  const descSavingRef = useRef<string | null>(null);

  // Suppression : tag en attente de confirmation + état d'envoi.
  const [pendingDelete, setPendingDelete] = useState<TaxonomyTag | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);

  // Fusion : ids sélectionnés (cases à cocher) + cible + confirmation.
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [mergeTargetId, setMergeTargetId] = useState<string | null>(null);
  const [confirmingMerge, setConfirmingMerge] = useState(false);
  const [mergeBusy, setMergeBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const reload = useCallback(
    (signal?: AbortSignal) => {
      setLoadError(false);
      fetchTaxonomy(libraryId, signal)
        .then((res) => setTags(res.tags))
        .catch(() => {
          if (signal?.aborted) return;
          setLoadError(true);
        });
    },
    [libraryId],
  );

  useEffect(() => {
    const controller = new AbortController();
    reload(controller.signal);
    return () => controller.abort();
  }, [reload]);

  /**
   * Enregistre la description d'un tag si elle a changé. Chaîne vide → efface (`null`).
   * Optimisme minimal : on recharge après succès (source de vérité = serveur).
   */
  const saveDescription = useCallback(
    async (tag: TaxonomyTag): Promise<void> => {
      if (descSavingRef.current !== null) return; // enregistrement déjà en vol
      const draft = descDraftsRef.current[tag.id];
      if (draft === undefined) return; // jamais touché → rien à faire
      const trimmed = draft.trim();
      const current = tag.description ?? '';
      if (trimmed === current) {
        // Inchangé : on oublie le brouillon, aucun appel réseau.
        setDescDrafts((prev) => {
          const next = { ...prev };
          delete next[tag.id];
          return next;
        });
        return;
      }
      descSavingRef.current = tag.id;
      setDescSavingId(tag.id);
      setDescErrorId(null);
      try {
        await setTagDescription(libraryId, tag.id, trimmed === '' ? null : trimmed);
        recordAction('taxonomy.describe');
        setDescDrafts((prev) => {
          const next = { ...prev };
          delete next[tag.id];
          return next;
        });
        reload();
      } catch {
        setDescErrorId(tag.id);
      } finally {
        descSavingRef.current = null;
        setDescSavingId(null);
      }
      // `reload` est stable pour une bibliothèque donnée ; le reste passe par les setters
      // `useState` (stables) ou les refs ci-dessus → identité constante.
    },
    [libraryId, reload],
  );

  // Nombre de tags candidats (pour l'étiquette du filtre « Masquer les candidats »).
  const candidateCount = useMemo(() => (tags ?? []).filter(isCandidate).length, [tags]);

  // Liste filtrée + triée pour l'affichage.
  const visible = useMemo(() => {
    if (tags === null) return [];
    const q = query.trim().toLowerCase();
    let filtered = q === '' ? tags : tags.filter((tag) => tag.label.toLowerCase().includes(q));
    if (hideCandidates) filtered = filtered.filter((tag) => !isCandidate(tag));
    return sortTags(filtered, sortKey);
  }, [tags, query, sortKey, hideCandidates]);

  const byId = useMemo(() => new Map((tags ?? []).map((tag) => [tag.id, tag])), [tags]);

  /** Traduit une `ApiError` de création/renommage en message localisé (409 = doublon). */
  const labelErrorMessage = useCallback(
    (err: unknown): string => {
      if (err instanceof ApiError && err.status === 409) return t('taxonomy.errorDuplicate');
      return t('taxonomy.errorGeneric');
    },
    [t],
  );

  // --- Création -----------------------------------------------------------
  const submitCreate = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    const label = newLabel.trim();
    if (label === '' || creating) return;
    setCreating(true);
    setCreateError(null);
    try {
      await createTag(libraryId, label);
      recordAction('taxonomy.create');
      setNewLabel('');
      reload();
    } catch (err) {
      setCreateError(labelErrorMessage(err));
    } finally {
      setCreating(false);
    }
  };

  // --- Renommage ----------------------------------------------------------
  const startEdit = useCallback((tag: TaxonomyTag): void => {
    setEditingId(tag.id);
    setEditLabel(tag.label);
    setEditError(null);
  }, []);
  const cancelEdit = useCallback((): void => {
    setEditingId(null);
    setEditLabel('');
    setEditError(null);
  }, []);
  /** Saisie du renommage : la ligne remonte sa valeur, on efface l'erreur affichée. */
  const changeEditLabel = useCallback((label: string): void => {
    setEditLabel(label);
    setEditError(null);
  }, []);
  // Le libellé est passé PAR LA LIGNE (et non lu dans l'état) pour que ce rappel reste
  // stable d'une frappe à l'autre — sinon toutes les lignes se re-rendraient.
  const submitEdit = useCallback(
    async (tag: TaxonomyTag, raw: string): Promise<void> => {
      const label = raw.trim();
      if (label === '' || editBusyRef.current) return;
      if (label === tag.label) {
        cancelEdit();
        return;
      }
      setEditBusy(true);
      setEditError(null);
      try {
        await renameTag(libraryId, tag.id, label);
        recordAction('taxonomy.rename');
        cancelEdit();
        reload();
      } catch (err) {
        setEditError(labelErrorMessage(err));
      } finally {
        setEditBusy(false);
      }
    },
    [libraryId, cancelEdit, reload, labelErrorMessage],
  );

  // Adaptateurs SYNCHRONES pour les lignes mémoïsées : les rappels d'une ligne ne doivent
  // rien rendre (une promesse flottante passée en prop d'événement est un piège classique).
  const onSubmitEditRow = useCallback(
    (tag: TaxonomyTag, label: string): void => void submitEdit(tag, label),
    [submitEdit],
  );
  const onSaveDescriptionRow = useCallback(
    (tag: TaxonomyTag): void => void saveDescription(tag),
    [saveDescription],
  );

  // --- Suppression --------------------------------------------------------
  const confirmDelete = async (): Promise<void> => {
    if (pendingDelete === null || deleteBusy) return;
    setDeleteBusy(true);
    setActionError(null);
    try {
      await deleteTag(libraryId, pendingDelete.id);
      recordAction('taxonomy.delete');
      setSelected((prev) => {
        const next = new Set(prev);
        next.delete(pendingDelete.id);
        return next;
      });
      setPendingDelete(null);
      reload();
    } catch {
      setActionError(t('taxonomy.errorGeneric'));
    } finally {
      setDeleteBusy(false);
    }
  };

  // --- Fusion -------------------------------------------------------------
  const toggleSelected = useCallback((id: string): void => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
        // Si la cible sort de la sélection, on l'oublie (elle ne serait plus valide).
        setMergeTargetId((target) => (target === id ? null : target));
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  /** Saisie de la description d'un tag (brouillon local, enregistré au blur/Entrée). */
  const changeDescDraft = useCallback((tagId: string, value: string): void => {
    setDescErrorId(null);
    setDescDrafts((prev) => ({ ...prev, [tagId]: value }));
  }, []);

  const selectedTags = useMemo(
    () =>
      [...selected]
        .map((id) => byId.get(id))
        .filter((tag): tag is TaxonomyTag => tag !== undefined),
    [selected, byId],
  );
  const canMerge =
    selectedTags.length >= 2 && mergeTargetId !== null && selected.has(mergeTargetId);
  const mergeTarget = mergeTargetId !== null ? byId.get(mergeTargetId) : undefined;
  const mergeSources = selectedTags.filter((tag) => tag.id !== mergeTargetId);

  const confirmMerge = async (): Promise<void> => {
    if (!canMerge || mergeTargetId === null || mergeBusy) return;
    setMergeBusy(true);
    setActionError(null);
    try {
      await mergeTags(libraryId, {
        sourceTagIds: mergeSources.map((tag) => tag.id),
        targetTagId: mergeTargetId,
      });
      recordAction('taxonomy.merge');
      setSelected(new Set());
      setMergeTargetId(null);
      setConfirmingMerge(false);
      reload();
    } catch {
      setActionError(t('taxonomy.errorGeneric'));
    } finally {
      setMergeBusy(false);
    }
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {/* En-tête de page. */}
      <div className="flex items-center justify-between gap-2 border-b border-border pb-4">
        <h1 className="flex items-center gap-2 text-lg font-semibold text-fg">
          <TagIcon className="h-6 w-6" aria-hidden="true" />
          {t('taxonomy.title')}
        </h1>
        <button
          type="button"
          onClick={onClose}
          title={t('taxonomy.close')}
          aria-label={t('taxonomy.close')}
          className="rounded-md p-2 text-muted hover:bg-surface-2 hover:text-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
        >
          <ArrowUturnLeftIcon className="h-6 w-6" aria-hidden="true" />
        </button>
      </div>

      {/* Corps défilant (seul conteneur de défilement de la page). `relative` : ancre les
          descendants `sr-only`/`absolute` à ce conteneur, pas au document (cf. v0.48.0). */}
      <div className="relative min-h-0 flex-1 overflow-y-auto py-4">
        <div className="mx-auto flex max-w-3xl flex-col gap-6">
          <p className="text-sm text-muted">{t('taxonomy.intro')}</p>

          {/* Création. */}
          <form onSubmit={submitCreate} className="flex flex-col gap-1">
            <div className="flex flex-wrap items-center gap-2">
              <label htmlFor="taxonomy-new" className="sr-only">
                {t('taxonomy.createLabel')}
              </label>
              <input
                id="taxonomy-new"
                type="text"
                value={newLabel}
                maxLength={TAG_LABEL_MAX}
                onChange={(e) => {
                  setNewLabel(e.target.value);
                  setCreateError(null);
                }}
                placeholder={t('taxonomy.createPlaceholder')}
                className={`${inputClass} min-w-0 flex-1`}
              />
              <button
                type="submit"
                disabled={newLabel.trim() === '' || creating}
                className="inline-flex h-11 items-center gap-1.5 rounded-md bg-brand px-4 text-sm font-medium text-brand-fg hover:opacity-90 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand disabled:cursor-not-allowed disabled:opacity-50"
              >
                <PlusIcon className="h-5 w-5" aria-hidden="true" />
                {t('taxonomy.createAction')}
              </button>
            </div>
            {createError !== null && (
              <p role="alert" className="flex items-center gap-1.5 text-sm text-danger">
                <ExclamationTriangleIcon className="h-4 w-4 shrink-0" aria-hidden="true" />
                {createError}
              </p>
            )}
          </form>

          {/* Barre : recherche + tri. */}
          <div className="flex flex-wrap items-center gap-2 border-y border-border py-3">
            <div className="relative min-w-0 flex-1">
              <MagnifyingGlassIcon
                className="pointer-events-none absolute start-2 top-1/2 h-5 w-5 -translate-y-1/2 text-muted"
                aria-hidden="true"
              />
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                aria-label={t('taxonomy.searchLabel')}
                placeholder={t('taxonomy.searchPlaceholder')}
                className={`${inputClass} w-full ps-9`}
              />
            </div>
            <label htmlFor="taxonomy-sort" className="text-sm text-muted">
              {t('taxonomy.sortLabel')}
            </label>
            <select
              id="taxonomy-sort"
              value={sortKey}
              onChange={(e) => setSortKey(e.target.value === 'usage' ? 'usage' : 'name')}
              className={`${inputClass} pe-8`}
            >
              <option value="name">{t('taxonomy.sortName')}</option>
              <option value="usage">{t('taxonomy.sortUsage')}</option>
            </select>
            {/* Masquer les tags candidats (proposés par l'IA, jamais validés). Visible
                seulement s'il en existe. */}
            {candidateCount > 0 && (
              <label className="inline-flex items-center gap-1.5 text-sm text-fg">
                <input
                  type="checkbox"
                  checked={hideCandidates}
                  onChange={(e) => setHideCandidates(e.target.checked)}
                  className="h-4 w-4 accent-brand"
                />
                {interpolate(t('taxonomy.hideCandidates'), { count: candidateCount }, locale)}
              </label>
            )}
          </div>

          {/* Barre de fusion (visible dès qu'au moins un tag est coché). */}
          {selected.size > 0 && (
            <div className="flex flex-col gap-2 rounded-md border border-border bg-surface-2 p-3">
              <p className="text-sm text-fg">
                {interpolate(t('taxonomy.mergeSelected'), { count: selected.size }, locale)}
              </p>
              <div className="flex flex-wrap items-center gap-2">
                <label htmlFor="taxonomy-merge-target" className="text-sm text-muted">
                  {t('taxonomy.mergeTargetLabel')}
                </label>
                <select
                  id="taxonomy-merge-target"
                  value={mergeTargetId ?? ''}
                  onChange={(e) => setMergeTargetId(e.target.value === '' ? null : e.target.value)}
                  className={`${inputClass} min-w-0 max-w-full pe-8`}
                >
                  <option value="">{t('taxonomy.mergeTargetPlaceholder')}</option>
                  {selectedTags.map((tag) => (
                    <option key={tag.id} value={tag.id}>
                      {tag.label}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={() => setConfirmingMerge(true)}
                  disabled={!canMerge}
                  className="inline-flex h-11 items-center gap-1.5 rounded-md bg-brand px-4 text-sm font-medium text-brand-fg hover:opacity-90 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <ArrowsRightLeftIcon className="h-5 w-5" aria-hidden="true" />
                  {t('taxonomy.mergeAction')}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setSelected(new Set());
                    setMergeTargetId(null);
                  }}
                  className="inline-flex h-11 items-center gap-1 rounded-md border border-border px-3 text-sm font-medium text-fg hover:bg-surface focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
                >
                  <XMarkIcon className="h-4 w-4" aria-hidden="true" />
                  {t('taxonomy.mergeClear')}
                </button>
              </div>
              {selected.size < 2 && <p className="text-xs text-muted">{t('taxonomy.mergeHint')}</p>}
            </div>
          )}

          {actionError !== null && (
            <p role="alert" className="flex items-center gap-1.5 text-sm text-danger">
              <ExclamationTriangleIcon className="h-4 w-4 shrink-0" aria-hidden="true" />
              {actionError}
            </p>
          )}

          {/* Liste. */}
          {loadError ? (
            <p role="alert" className="flex items-center gap-1.5 text-sm text-danger">
              <ExclamationTriangleIcon className="h-5 w-5 shrink-0" aria-hidden="true" />
              {t('taxonomy.loadError')}
            </p>
          ) : tags === null ? (
            <p className="text-sm text-muted">{t('taxonomy.loading')}</p>
          ) : visible.length === 0 ? (
            <p className="text-sm text-muted">
              {tags.length === 0 ? t('taxonomy.empty') : t('taxonomy.noMatch')}
            </p>
          ) : (
            <ul className="flex flex-col divide-y divide-border rounded-md border border-border">
              {visible.map((tag) => (
                <TaxonomyRow
                  key={tag.id}
                  tag={tag}
                  isChecked={selected.has(tag.id)}
                  isEditing={editingId === tag.id}
                  descValue={descValueOf(tag)}
                  descSaving={descSavingId === tag.id}
                  descError={descErrorId === tag.id}
                  /* Les états d'ÉDITION ne sont transmis qu'à la ligne en cours d'édition :
                     les autres reçoivent des constantes, donc leur mémoïsation survit à
                     chaque frappe dans le champ de renommage. */
                  editLabel={editingId === tag.id ? editLabel : ''}
                  editError={editingId === tag.id ? editError : null}
                  editBusy={editingId === tag.id ? editBusy : false}
                  onToggleSelected={toggleSelected}
                  onStartEdit={startEdit}
                  onCancelEdit={cancelEdit}
                  onEditLabelChange={changeEditLabel}
                  onSubmitEdit={onSubmitEditRow}
                  onDescChange={changeDescDraft}
                  onSaveDescription={onSaveDescriptionRow}
                  onRequestDelete={setPendingDelete}
                />
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Confirmation de SUPPRESSION (avertit si le tag est utilisé). */}
      {pendingDelete !== null && (
        <Portal>
          <ConfirmModal
            title={t('taxonomy.deleteTitle')}
            confirmLabel={t('taxonomy.deleteConfirm')}
            cancelLabel={t('taxonomy.cancel')}
            busyLabel={t('taxonomy.deleting')}
            busy={deleteBusy}
            danger
            onConfirm={() => void confirmDelete()}
            onClose={() => {
              if (!deleteBusy) setPendingDelete(null);
            }}
          >
            <p>{interpolate(t('taxonomy.deleteBody'), { label: pendingDelete.label })}</p>
            {pendingDelete.usageCount > 0 && (
              <p className="flex items-start gap-2 rounded-md border border-danger/40 bg-surface-2 p-3">
                <ExclamationTriangleIcon
                  className="mt-0.5 h-5 w-5 shrink-0 text-danger"
                  aria-hidden="true"
                />
                <span>
                  {interpolate(
                    t('taxonomy.deleteUsedWarning'),
                    {
                      count: pendingDelete.usageCount,
                    },
                    locale,
                  )}
                </span>
              </p>
            )}
          </ConfirmModal>
        </Portal>
      )}

      {/* Confirmation de FUSION. */}
      {confirmingMerge && mergeTarget !== undefined && (
        <Portal>
          <ConfirmModal
            title={t('taxonomy.mergeTitle')}
            confirmLabel={t('taxonomy.mergeConfirm')}
            cancelLabel={t('taxonomy.cancel')}
            busyLabel={t('taxonomy.merging')}
            busy={mergeBusy}
            onConfirm={() => void confirmMerge()}
            onClose={() => {
              if (!mergeBusy) setConfirmingMerge(false);
            }}
          >
            <p>
              {interpolate(
                t('taxonomy.mergeBody'),
                {
                  count: mergeSources.length,
                  sources: mergeSources.map((tag) => tag.label).join(', '),
                  target: mergeTarget.label,
                },
                locale,
              )}
            </p>
            <p className="text-muted">{t('taxonomy.mergeNote')}</p>
          </ConfirmModal>
        </Portal>
      )}
    </div>
  );
}

/**
 * UNE ligne de la liste des tags — composant MÉMOÏSÉ (audit B4).
 *
 * `TaxonomyPage` n'a aucune pagination : toute la taxonomie est rendue d'un bloc. Sans
 * mémoïsation, chaque frappe dans le champ de RECHERCHE re-rendait l'INTÉGRALITÉ des
 * lignes (une taxonomie enrichie par l'IA en compte facilement plusieurs centaines), alors
 * que la recherche ne fait que RÉDUIRE la liste : les lignes conservées sont identiques.
 * Ici, seules les lignes dont une prop change effectivement se re-rendent.
 *
 * Pour que la mémoïsation TIENNE, tous les rappels reçus sont stables (`useCallback` +
 * miroirs `ref` côté page) et les états d'édition ne sont transmis qu'à la ligne
 * concernée. Même patron que `gallery/Thumbnail.tsx`.
 *
 * VIRTUALISATION NON RETENUE (jugement demandé à l'audit) : elle imposerait un conteneur
 * de défilement dédié et une mesure de hauteur PAR LIGNE — les lignes s'agrandissent
 * (formulaire de renommage, message d'erreur en `basis-full`) — pour un écran
 * d'ADMINISTRATION consulté ponctuellement, à l'échelle de quelques centaines d'entrées.
 * Sans commune mesure avec la galerie, qui doit tenir 30 000 vignettes. La mémoïsation
 * suffit à rendre la frappe fluide.
 */
interface TaxonomyRowProps {
  tag: TaxonomyTag;
  isChecked: boolean;
  isEditing: boolean;
  descValue: string;
  descSaving: boolean;
  descError: boolean;
  editLabel: string;
  editError: string | null;
  editBusy: boolean;
  onToggleSelected: (id: string) => void;
  onStartEdit: (tag: TaxonomyTag) => void;
  onCancelEdit: () => void;
  onEditLabelChange: (label: string) => void;
  onSubmitEdit: (tag: TaxonomyTag, label: string) => void;
  onDescChange: (tagId: string, value: string) => void;
  onSaveDescription: (tag: TaxonomyTag) => void;
  onRequestDelete: (tag: TaxonomyTag) => void;
}

const TaxonomyRow = memo(function TaxonomyRow({
  tag,
  isChecked,
  isEditing,
  descValue,
  descSaving,
  descError,
  editLabel,
  editError,
  editBusy,
  onToggleSelected,
  onStartEdit,
  onCancelEdit,
  onEditLabelChange,
  onSubmitEdit,
  onDescChange,
  onSaveDescription,
  onRequestDelete,
}: TaxonomyRowProps): React.JSX.Element {
  // `t` est récupéré ICI plutôt que passé en prop : son identité (stable par locale) ne
  // participe donc pas à la comparaison de mémoïsation.
  const { t, locale } = useI18n();
  return (
    <li key={tag.id} className="flex flex-wrap items-center gap-2 px-3 py-2">
      {/* Case de sélection pour la fusion. */}
      <input
        type="checkbox"
        checked={isChecked}
        onChange={() => onToggleSelected(tag.id)}
        aria-label={interpolate(t('taxonomy.selectForMerge'), { label: tag.label })}
        className="h-4 w-4 shrink-0 accent-brand"
      />

      {isEditing ? (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            onSubmitEdit(tag, editLabel);
          }}
          className="flex min-w-0 flex-1 flex-wrap items-center gap-2"
        >
          <label htmlFor={`edit-${tag.id}`} className="sr-only">
            {t('taxonomy.renameLabel')}
          </label>
          <input
            id={`edit-${tag.id}`}
            type="text"
            value={editLabel}
            maxLength={TAG_LABEL_MAX}
            autoFocus
            onChange={(e) => {
              onEditLabelChange(e.target.value);
            }}
            className={`${inputClass} min-w-0 flex-1`}
          />
          <button
            type="submit"
            disabled={editLabel.trim() === '' || editBusy}
            className="inline-flex h-9 items-center gap-1 rounded-md bg-brand px-3 text-sm font-medium text-brand-fg hover:opacity-90 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand disabled:opacity-50"
          >
            <CheckIcon className="h-4 w-4" aria-hidden="true" />
            {t('taxonomy.renameSave')}
          </button>
          <button
            type="button"
            onClick={onCancelEdit}
            className="inline-flex h-9 items-center gap-1 rounded-md border border-border px-3 text-sm font-medium text-fg hover:bg-surface-2 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
          >
            <XMarkIcon className="h-4 w-4" aria-hidden="true" />
            {t('taxonomy.renameCancel')}
          </button>
          {editError !== null && (
            <span role="alert" className="basis-full text-sm text-danger">
              {editError}
            </span>
          )}
        </form>
      ) : (
        <>
          {/* Libellé du tag — LARGEUR FIXE pour aligner la colonne Description
                de toutes les lignes (les libellés ont des largeurs variables) ;
                tronqué avec `title` complet au survol si trop long. */}
          <span className="w-32 shrink-0 truncate font-medium text-fg" title={tag.label}>
            {tag.label}
          </span>

          {/* Description inline (définition libre optionnelle qui guide l'IA de
                tagging) — sur la MÊME ligne, occupe la place restante ; vide par
                défaut. Enregistrée à la validation (Entrée) ou à la perte de focus. */}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              onSaveDescription(tag);
            }}
            className="flex min-w-0 flex-1 items-center gap-1.5"
          >
            <label htmlFor={`desc-${tag.id}`} className="sr-only">
              {interpolate(t('taxonomy.descriptionAria'), { label: tag.label })}
            </label>
            <input
              id={`desc-${tag.id}`}
              type="text"
              value={descValue}
              maxLength={TAG_DESCRIPTION_MAX}
              disabled={descSaving}
              onChange={(e) => {
                onDescChange(tag.id, e.target.value);
              }}
              onBlur={() => onSaveDescription(tag)}
              placeholder={t('taxonomy.descriptionPlaceholder')}
              className="h-8 min-w-0 flex-1 rounded-md border border-border bg-surface px-2 text-sm text-fg placeholder:text-muted focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand disabled:opacity-50"
            />
            {descSaving && (
              <span className="shrink-0 text-xs text-muted">{t('taxonomy.descriptionSaving')}</span>
            )}
          </form>

          {/* Badge « Candidat » (proposé par l'IA, jamais validé) : icône + texte. */}
          {isCandidate(tag) && (
            <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-surface-2 px-2 py-0.5 text-xs font-medium text-fg">
              <SparklesIcon className="h-3.5 w-3.5" aria-hidden="true" />
              {t('taxonomy.candidate')}
            </span>
          )}

          {/* Nombre d'usages. */}
          <span className="shrink-0 rounded-full bg-surface-2 px-2 py-0.5 text-xs text-muted">
            {tag.usageCount === 0
              ? t('taxonomy.usageNone')
              : interpolate(t('taxonomy.usageCount'), { count: tag.usageCount }, locale)}
          </span>

          <button
            type="button"
            onClick={() => onStartEdit(tag)}
            title={t('taxonomy.renameAction')}
            aria-label={interpolate(t('taxonomy.renameAria'), { label: tag.label })}
            className="shrink-0 rounded-md p-1.5 text-muted hover:bg-surface-2 hover:text-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
          >
            <PencilSquareIcon className="h-5 w-5" aria-hidden="true" />
          </button>
          <button
            type="button"
            onClick={() => onRequestDelete(tag)}
            title={t('taxonomy.deleteAction')}
            aria-label={interpolate(t('taxonomy.deleteAria'), { label: tag.label })}
            className="shrink-0 rounded-md p-1.5 text-muted hover:bg-surface-2 hover:text-danger focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-danger"
          >
            <TrashIcon className="h-5 w-5" aria-hidden="true" />
          </button>

          {/* Erreur d'enregistrement de la description (rare) → passe à la ligne. */}
          {descError && (
            <span role="alert" className="flex basis-full items-center gap-1.5 text-sm text-danger">
              <ExclamationTriangleIcon className="h-4 w-4 shrink-0" aria-hidden="true" />
              {t('taxonomy.errorGeneric')}
            </span>
          )}
        </>
      )}
    </li>
  );
});
